(() => {
  const SELECTOR = "[data-op-section]";
  const VIDEO_SELECTOR = "[data-op-video]";
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const connection =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const conservativeData = Boolean(
    connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || ""),
  );
  const videoVisibility = new Map();
  let activeVideo = null;
  let preferredVideo = null;
  let instagramSdkPromise;
  let prewarmObserver;
  let playbackObserver;
  let selectionQueued = false;

  const reveal = (scope) => {
    const items = scope.querySelectorAll(
      "[data-op-reveal]:not([data-op-bound])",
    );
    if (!items.length) return;
    if (document.querySelector("[data-op-home-motion]") || reduceMotion.matches || !("IntersectionObserver" in window)) {
      items.forEach((item) => {
        item.dataset.opBound = "true";
        item.classList.add("is-visible");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 },
    );

    items.forEach((item) => {
      item.dataset.opBound = "true";
      observer.observe(item);
    });
  };

  const isRenderableVideo = (video) =>
    video.isConnected &&
    !video.hidden &&
    window.getComputedStyle(video).display !== "none";

  const updateUgcSoundButton = (video) => {
    const card = video.closest("[data-op-ugc-card]");
    const button = card?.querySelector("[data-op-ugc-sound]");
    if (!button) return;

    const hasSound = !video.muted;
    button.setAttribute("aria-pressed", String(hasSound));
    button.setAttribute(
      "aria-label",
      hasSound ? "Silenciar video" : "Activar audio",
    );
    button
      .querySelector("[data-op-sound-off]")
      ?.toggleAttribute("hidden", hasSound);
    button
      .querySelector("[data-op-sound-on]")
      ?.toggleAttribute("hidden", !hasSound);
  };

  const muteOtherUgcVideos = (activeUgcVideo) => {
    document.querySelectorAll('[data-op-video-kind="ugc"]').forEach((video) => {
      if (video === activeUgcVideo) return;
      video.muted = true;
      updateUgcSoundButton(video);
    });
  };

  const hydrateVideo = (video, preload = "metadata") => {
    if (!isRenderableVideo(video)) return false;

    if (video.dataset.opVideoHydrated === "true") {
      if (video.preload === "none" && preload !== "none") {
        video.preload = preload;
      }
      return true;
    }

    const sources = Array.from(video.querySelectorAll("source[data-src]"));
    if (!sources.length) return false;

    const mp4 = sources.filter(source => source.type === "video/mp4" && Number(source.dataset.height) > 0);
    const nativeStream = sources.some(source => source.type.includes("mpegurl") && video.canPlayType(source.type));
    let selectedMp4 = null;
    if (!nativeStream && mp4.length > 1) {
      const rect = video.getBoundingClientRect();
      const targetHeight = rect.height * Math.min(window.devicePixelRatio || 1, conservativeData ? 1 : 1.5);
      mp4.sort((a, b) => Number(a.dataset.height) - Number(b.dataset.height));
      selectedMp4 = mp4.find(source => Number(source.dataset.height) >= targetHeight) || mp4[mp4.length - 1];
      video.insertBefore(selectedMp4, video.firstChild);
    }
    sources.forEach((source) => {
      if (selectedMp4 && mp4.includes(source) && source !== selectedMp4) return;
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });
    video.preload = preload;
    video.dataset.opVideoHydrated = "true";
    video.dataset.opVideoState = "loading";

    const markReady = () => {
      video.dataset.opVideoState = "ready";
      video.classList.add("is-ready");
    };
    const markError = () => {
      video.dataset.opVideoState = "error";
      video.classList.remove("is-ready");
    };

    video.addEventListener("loadeddata", markReady, { once: true });
    video.addEventListener("error", markError, { once: true });
    video.load();
    if (video.readyState >= 2) markReady();
    return true;
  };

  const pauseVideo = (video, muteUgc = true) => {
    if (!video) return;
    video.pause();
    video.dataset.opVideoState =
      video.dataset.opVideoHydrated === "true" ? "paused" : "idle";
    if (muteUgc && video.dataset.opVideoKind === "ugc") {
      video.muted = true;
      updateUgcSoundButton(video);
    }
  };

  const playVideo = (video) => {
    if (
      !video ||
      reduceMotion.matches ||
      document.visibilityState === "hidden" ||
      !isRenderableVideo(video)
    ) {
      return;
    }

    const preload = "metadata";
    if (!hydrateVideo(video, preload)) return;

    if (video.dataset.opVideoKind !== "ugc") {
      video.muted = true;
    }
    const playPromise = video.play();
    video.dataset.opVideoState = "playing";
    playPromise?.catch?.(() => {
      video.dataset.opVideoState = "paused";
    });
  };

  const minimumVisibleRatio = (video) =>
    video.dataset.opVideoKind === "ugc" ? 0.52 : 0.3;

  const activateVideo = (video, { prefer = false } = {}) => {
    if (!video || !isRenderableVideo(video)) return;
    if (prefer) preferredVideo = video;

    if (video === activeVideo) {
      if (video.paused) playVideo(video);
      return;
    }

    pauseVideo(activeVideo);
    activeVideo = video;
    if (video.dataset.opVideoKind === "ugc") {
      video.muted = true;
      muteOtherUgcVideos(video);
      updateUgcSoundButton(video);
    }
    playVideo(video);
  };

  const chooseActiveVideo = () => {
    selectionQueued = false;

    if (reduceMotion.matches || document.visibilityState === "hidden") {
      pauseVideo(activeVideo);
      activeVideo = null;
      return;
    }

    const candidates = Array.from(videoVisibility.entries())
      .filter(
        ([video, ratio]) =>
          isRenderableVideo(video) && ratio >= minimumVisibleRatio(video),
      )
      .sort((a, b) => b[1] - a[1]);

    if (
      preferredVideo &&
      (!isRenderableVideo(preferredVideo) ||
        (videoVisibility.get(preferredVideo) || 0) < 0.16)
    ) {
      preferredVideo = null;
    }

    let nextVideo = preferredVideo || candidates[0]?.[0] || null;
    if (activeVideo && isRenderableVideo(activeVideo)) {
      const activeRatio = videoVisibility.get(activeVideo) || 0;
      const bestRatio = candidates[0]?.[1] || 0;
      if (
        !preferredVideo &&
        activeRatio >= minimumVisibleRatio(activeVideo) &&
        bestRatio - activeRatio < 0.12
      ) {
        nextVideo = activeVideo;
      }
    }

    if (nextVideo === activeVideo) {
      if (nextVideo?.paused) playVideo(nextVideo);
      return;
    }

    if (!nextVideo) {
      pauseVideo(activeVideo);
      activeVideo = null;
      return;
    }

    activateVideo(nextVideo);
  };

  const scheduleVideoSelection = () => {
    if (selectionQueued) return;
    selectionQueued = true;
    window.requestAnimationFrame(chooseActiveVideo);
  };

  const ensureVideoObservers = () => {
    if (!("IntersectionObserver" in window) || prewarmObserver) return;

    prewarmObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || !isRenderableVideo(entry.target)) return;
          hydrateVideo(entry.target, conservativeData ? "none" : "metadata");
        });
      },
      {
        rootMargin: conservativeData ? "0px" : "250px 0px",
        threshold: 0.01,
      },
    );

    playbackObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          videoVisibility.set(
            entry.target,
            entry.isIntersecting ? entry.intersectionRatio : 0,
          );
        });
        scheduleVideoSelection();
      },
      { threshold: [0, 0.08, 0.2, 0.3, 0.4, 0.52, 0.7, 0.9, 1] },
    );
  };

  const registerVideos = (scope) => {
    const videos = [];
    if (scope.matches?.(`${VIDEO_SELECTOR}:not([data-op-video-bound])`)) {
      videos.push(scope);
    }
    scope
      .querySelectorAll?.(`${VIDEO_SELECTOR}:not([data-op-video-bound])`)
      .forEach((video) => videos.push(video));
    if (!videos.length) return;

    ensureVideoObservers();
    videos.forEach((video) => {
      video.dataset.opVideoBound = "true";
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      updateUgcSoundButton(video);

      if (video.dataset.opVideoPriority === "critical" && isRenderableVideo(video)) {
        hydrateVideo(video, "metadata");
      }

      if (prewarmObserver && playbackObserver) {
        prewarmObserver.observe(video);
        playbackObserver.observe(video);
      } else if (
        video.dataset.opVideoPriority === "critical" &&
        !reduceMotion.matches
      ) {
        videoVisibility.set(video, 1);
      }
    });
    scheduleVideoSelection();
  };

  const bindUgcSound = (scope) => {
    scope
      .querySelectorAll?.("[data-op-ugc-sound]:not([data-op-sound-bound])")
      .forEach((button) => {
        button.dataset.opSoundBound = "true";
        const video = button
          .closest("[data-op-ugc-card]")
          ?.querySelector('[data-op-video-kind="ugc"]');
        if (!video) return;

        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          const enableSound = video.muted;
          if (enableSound) {
            activateVideo(video, { prefer: true });
            muteOtherUgcVideos(video);
          }
          video.muted = !enableSound;
          updateUgcSoundButton(video);
        });
      });
  };

  const bindUgcCarousels = (scope) => {
    const sections = [];
    if (scope.matches?.("[data-op-ugc]:not([data-op-ugc-carousel-bound])")) {
      sections.push(scope);
    }
    scope
      .querySelectorAll?.(
        "[data-op-ugc]:not([data-op-ugc-carousel-bound])",
      )
      .forEach((section) => sections.push(section));

    sections.forEach((section) => {
      const rail = section.querySelector("[data-op-ugc-rail]");
      const previous = section.querySelector("[data-op-ugc-prev]");
      const next = section.querySelector("[data-op-ugc-next]");
      const controls = section.querySelector("[data-op-ugc-controls]");
      const status = section.querySelector("[data-op-ugc-status]");
      const cards = Array.from(section.querySelectorAll("[data-op-ugc-card]"));
      if (!rail || !previous || !next || !controls || !cards.length) return;

      section.dataset.opUgcCarouselBound = "true";
      const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
      let scrollTimer;
      let dragState = null;
      let resizeObserver;

      const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
      const clampScroll = (value) => Math.min(Math.max(value, 0), maxScroll());
      const closestIndex = (scrollLeft = rail.scrollLeft) => {
        const center = scrollLeft + rail.clientWidth / 2;
        let bestIndex = 0;
        let bestDistance = Number.POSITIVE_INFINITY;
        cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(cardCenter - center);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        });
        return bestIndex;
      };

      const updateControls = () => {
        const overflow = maxScroll() > 2;
        controls.hidden = !overflow;
        const atStart = rail.scrollLeft <= 2;
        const atEnd = rail.scrollLeft >= maxScroll() - 2;
        previous.disabled = !overflow || atStart;
        next.disabled = !overflow || atEnd;
        previous.setAttribute("aria-disabled", String(previous.disabled));
        next.setAttribute("aria-disabled", String(next.disabled));
      };

      const activateCard = (card, announce = true) => {
        if (!card) return;
        cards.forEach((item) =>
          item.classList.toggle("is-active", item === card),
        );
        const video = card.querySelector('[data-op-video-kind="ugc"]');
        if (video) activateVideo(video, { prefer: true });
        if (announce && status) {
          const index = cards.indexOf(card);
          status.textContent = `Video ${index + 1} de ${cards.length}`;
        }
      };

      const scrollToIndex = (index) => {
        const safeIndex = Math.min(Math.max(index, 0), cards.length - 1);
        const card = cards[safeIndex];
        const target = clampScroll(
          card.offsetLeft - (rail.clientWidth - card.offsetWidth) / 2,
        );
        activateCard(card);
        rail.scrollTo({
          left: target,
          behavior: reduceMotion.matches ? "auto" : "smooth",
        });
      };

      const selectClosest = () => {
        updateControls();
        activateCard(cards[closestIndex()]);
      };

      previous.addEventListener("click", () =>
        scrollToIndex(closestIndex() - 1),
      );
      next.addEventListener("click", () =>
        scrollToIndex(closestIndex() + 1),
      );

      rail.addEventListener(
        "scroll",
        () => {
          updateControls();
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(selectClosest, 120);
        },
        { passive: true },
      );

      rail.addEventListener("keydown", (event) => {
        if (event.target.closest("a, button, input, select, textarea")) return;
        const current = closestIndex();
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollToIndex(current - 1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollToIndex(current + 1);
        } else if (event.key === "Home") {
          event.preventDefault();
          scrollToIndex(0);
        } else if (event.key === "End") {
          event.preventDefault();
          scrollToIndex(cards.length - 1);
        }
      });

      cards.forEach((card) => {
        card.addEventListener("pointerenter", (event) => {
          if (!finePointer.matches || event.pointerType === "touch") return;
          activateCard(card, false);
        });
        card.addEventListener("focusin", () => activateCard(card, false));
      });

      const finishDrag = (event, cancelled = false) => {
        if (!dragState || dragState.pointerId !== event.pointerId) return;
        const wasDragging = dragState.dragging;
        const projected = cancelled
          ? rail.scrollLeft
          : rail.scrollLeft - dragState.velocity * 180;
        if (wasDragging) {
          rail.classList.remove("is-dragging");
          if (rail.hasPointerCapture?.(event.pointerId)) {
            rail.releasePointerCapture(event.pointerId);
          }
          scrollToIndex(closestIndex(projected));
        }
        dragState = null;
      };

      rail.addEventListener("pointerdown", (event) => {
        if (
          event.pointerType !== "mouse" ||
          event.button !== 0 ||
          event.target.closest("a, button, input, select, textarea, iframe")
        ) {
          return;
        }
        dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScroll: rail.scrollLeft,
          lastX: event.clientX,
          lastTime: performance.now(),
          velocity: 0,
          dragging: false,
        };
      });

      rail.addEventListener("pointermove", (event) => {
        if (!dragState || dragState.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - dragState.startX;
        if (!dragState.dragging && Math.abs(deltaX) < 7) return;
        if (!dragState.dragging) {
          dragState.dragging = true;
          rail.classList.add("is-dragging");
          rail.setPointerCapture?.(event.pointerId);
        }

        const now = performance.now();
        const elapsed = Math.max(1, now - dragState.lastTime);
        dragState.velocity = (event.clientX - dragState.lastX) / elapsed;
        dragState.lastX = event.clientX;
        dragState.lastTime = now;
        rail.scrollLeft = dragState.startScroll - deltaX;
        event.preventDefault();
      });

      rail.addEventListener("pointerup", (event) => finishDrag(event));
      rail.addEventListener("pointercancel", (event) =>
        finishDrag(event, true),
      );

      if ("ResizeObserver" in window) {
        resizeObserver = new ResizeObserver(updateControls);
        resizeObserver.observe(rail);
      } else {
        window.addEventListener("resize", updateControls, { passive: true });
      }

      section._opUgcCarouselCleanup = () => {
        window.clearTimeout(scrollTimer);
        resizeObserver?.disconnect();
        if (!("ResizeObserver" in window)) {
          window.removeEventListener("resize", updateControls);
        }
      };

      cards[0]?.classList.add("is-active");
      updateControls();
    });
  };

  const bindRails = (scope) => {
    scope
      .querySelectorAll(".op-products:not([data-op-rail-bound])")
      .forEach((section) => {
        const rail = section.querySelector("[data-op-rail]");
        const previous = section.querySelector("[data-op-rail-prev]");
        const next = section.querySelector("[data-op-rail-next]");
        if (!rail || !previous || !next) return;
        section.dataset.opRailBound = "true";
        let dragState = null;
        let suppressClickUntil = 0;

        const move = (direction) => {
          const item = rail.querySelector(".op-product-rail__item");
          const styles = window.getComputedStyle(rail);
          const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
          const distance = item
            ? item.getBoundingClientRect().width + gap
            : rail.clientWidth * 0.8;
          rail.scrollBy({
            left: distance * direction,
            behavior: reduceMotion.matches ? "auto" : "smooth",
          });
        };

        const items = () =>
          Array.from(rail.querySelectorAll(".op-product-rail__item"));
        const maxScroll = () =>
          Math.max(0, rail.scrollWidth - rail.clientWidth);
        const itemScrollLeft = (item) => {
          const railRect = rail.getBoundingClientRect();
          const itemRect = item.getBoundingClientRect();
          return rail.scrollLeft + itemRect.left - railRect.left;
        };
        const closestItem = (projectedScrollLeft = rail.scrollLeft) => {
          const railItems = items();
          if (!railItems.length) return null;
          return railItems.reduce((closest, item) =>
            Math.abs(itemScrollLeft(item) - projectedScrollLeft) <
            Math.abs(itemScrollLeft(closest) - projectedScrollLeft)
              ? item
              : closest,
          );
        };
        const snapToClosest = (projectedScrollLeft = rail.scrollLeft) => {
          const item = closestItem(projectedScrollLeft);
          if (!item) return;
          const target = Math.min(
            Math.max(itemScrollLeft(item), 0),
            maxScroll(),
          );
          rail.scrollTo({
            left: target,
            behavior: reduceMotion.matches ? "auto" : "smooth",
          });
        };

        const updateControls = () => {
          const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
          const atStart = rail.scrollLeft <= 2;
          const atEnd = rail.scrollLeft >= maxScroll - 2;
          previous.disabled = atStart;
          next.disabled = atEnd;
          previous.setAttribute("aria-disabled", String(atStart));
          next.setAttribute("aria-disabled", String(atEnd));
        };

        previous.addEventListener("click", () => move(-1));
        next.addEventListener("click", () => move(1));
        rail.addEventListener("scroll", updateControls, { passive: true });
        rail.addEventListener("dragstart", (event) => event.preventDefault());
        rail.addEventListener("selectstart", (event) => {
          if (dragState) event.preventDefault();
        });
        rail.addEventListener(
          "click",
          (event) => {
            if (performance.now() >= suppressClickUntil) return;
            event.preventDefault();
            event.stopPropagation();
          },
          true,
        );

        rail.addEventListener("pointerdown", (event) => {
          if (
            event.pointerType !== "mouse" ||
            event.button !== 0 ||
            event.target.closest("button, input, select, textarea, summary")
          ) {
            return;
          }

          rail.scrollTo({ left: rail.scrollLeft, behavior: "auto" });
          dragState = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startScroll: rail.scrollLeft,
            lastX: event.clientX,
            lastTime: performance.now(),
            velocity: 0,
            dragging: false,
          };
        });

        rail.addEventListener("pointermove", (event) => {
          if (!dragState || dragState.pointerId !== event.pointerId) return;
          const deltaX = event.clientX - dragState.startX;
          if (!dragState.dragging && Math.abs(deltaX) < 7) return;

          if (!dragState.dragging) {
            dragState.dragging = true;
            rail.classList.add("is-dragging");
            rail.setPointerCapture?.(event.pointerId);
          }

          const now = performance.now();
          const elapsed = Math.max(1, now - dragState.lastTime);
          dragState.velocity = (event.clientX - dragState.lastX) / elapsed;
          dragState.lastX = event.clientX;
          dragState.lastTime = now;
          rail.scrollLeft = dragState.startScroll - deltaX;
          event.preventDefault();
        });

        const finishDrag = (event, cancelled = false) => {
          if (!dragState || dragState.pointerId !== event.pointerId) return;
          if (dragState.dragging) {
            const projected = cancelled
              ? rail.scrollLeft
              : rail.scrollLeft - dragState.velocity * 180;
            suppressClickUntil = performance.now() + 250;
            rail.classList.remove("is-dragging");
            if (rail.hasPointerCapture?.(event.pointerId)) {
              rail.releasePointerCapture(event.pointerId);
            }
            snapToClosest(projected);
          }
          dragState = null;
        };

        rail.addEventListener("pointerup", (event) => finishDrag(event));
        rail.addEventListener("pointercancel", (event) =>
          finishDrag(event, true),
        );
        window.addEventListener("resize", updateControls, { passive: true });
        updateControls();
      });
  };

  const bindLook = (scope) => {
    scope
      .querySelectorAll("[data-op-look]:not([data-op-bound])")
      .forEach((look) => {
        look.dataset.opBound = "true";
        const hotspots = Array.from(look.querySelectorAll("[data-op-hotspot]"));
        const products = Array.from(
          look.querySelectorAll("[data-op-look-product]"),
        );
        hotspots.forEach((hotspot) => {
          hotspot.addEventListener("click", () => {
            const key = hotspot.dataset.opHotspot;
            hotspots.forEach((item) => {
              const active = item === hotspot;
              item.classList.toggle("is-active", active);
              item.setAttribute("aria-pressed", String(active));
            });
            products.forEach((product) =>
              product.classList.toggle(
                "is-active",
                product.dataset.opLookProduct === key,
              ),
            );
          });
        });
      });
  };

  const loadInstagramSdk = () => {
    if (window.instgrm?.Embeds?.process) return Promise.resolve();
    if (instagramSdkPromise) return instagramSdkPromise;

    instagramSdkPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(
        'script[src*="instagram.com/embed.js"]',
      );
      const onLoad = () => resolve();
      const onError = () => reject(new Error("Instagram embed unavailable"));

      if (!script) {
        script = document.createElement("script");
        script.src = "https://www.instagram.com/embed.js";
        script.async = true;
        script.defer = true;
        script.dataset.opInstagramSdk = "true";
        document.body.appendChild(script);
      }

      script.addEventListener("load", onLoad, { once: true });
      script.addEventListener("error", onError, { once: true });
    });

    return instagramSdkPromise;
  };

  const processInstagram = () =>
    loadInstagramSdk()
      .then(() => window.instgrm?.Embeds?.process?.())
      .catch(() => {});

  const bindInstagram = (scope) => {
    const sections = [];
    if (scope.matches?.("[data-op-ugc]")) sections.push(scope);
    scope
      .querySelectorAll?.("[data-op-ugc]:not([data-op-instagram-bound])")
      .forEach((section) => sections.push(section));

    sections.forEach((section) => {
      if (!section.querySelector(".instagram-media[data-instgrm-permalink]"))
        return;
      section.dataset.opInstagramBound = "true";

      if (!("IntersectionObserver" in window)) {
        processInstagram();
        return;
      }

      const observer = new IntersectionObserver(
        (entries, currentObserver) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          processInstagram();
          currentObserver.disconnect();
        },
        { rootMargin: "500px 0px", threshold: 0.01 },
      );
      observer.observe(section);
    });
  };

  const init = (scope = document) => {
    reveal(scope);
    registerVideos(scope);
    bindUgcSound(scope);
    bindUgcCarousels(scope);
    bindRails(scope);
    bindLook(scope);
    bindInstagram(scope);
  };

  const reevaluateVideos = () => {
    if (activeVideo && !isRenderableVideo(activeVideo)) {
      pauseVideo(activeVideo);
      activeVideo = null;
    }
    if (preferredVideo && !isRenderableVideo(preferredVideo)) {
      preferredVideo = null;
    }
    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      if (
        video.dataset.opVideoPriority === "critical" &&
        isRenderableVideo(video)
      ) {
        hydrateVideo(video, "metadata");
      }
    });
    scheduleVideoSelection();
  };

  const onReady = () => init(document);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady, { once: true });
  } else {
    onReady();
  }

  document.addEventListener("shopify:section:load", (event) =>
    init(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    const ugcSections = [];
    if (event.target.matches?.("[data-op-ugc]")) ugcSections.push(event.target);
    event.target
      .querySelectorAll?.("[data-op-ugc]")
      .forEach((section) => ugcSections.push(section));
    ugcSections.forEach((section) => section._opUgcCarouselCleanup?.());
    event.target.querySelectorAll?.(VIDEO_SELECTOR).forEach((video) => {
      prewarmObserver?.unobserve(video);
      playbackObserver?.unobserve(video);
      videoVisibility.delete(video);
      if (video === activeVideo) {
        pauseVideo(video);
        activeVideo = null;
      }
      if (video === preferredVideo) preferredVideo = null;
    });
    scheduleVideoSelection();
  });
  document.addEventListener("visibilitychange", reevaluateVideos);
  window.addEventListener("resize", reevaluateVideos, { passive: true });
  window.addEventListener("pagehide", () => {
    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) =>
      pauseVideo(video),
    );
  });
  reduceMotion.addEventListener?.("change", reevaluateVideos);

  window.OroPlataHome = {
    init,
    selector: SELECTOR,
    videoMode: conservativeData ? "adaptive-data" : "cinematic",
  };
})();
